// Copy for the service-area landing pages. Each page must stay genuinely distinct (not find-and-replace),
// and must only make claims about Flip Ready that the homepage already makes.

export interface AreaPageContent {
  path: string;
  /** Short name used in breadcrumbs and headings, e.g. "Stafford" */
  name: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  highlightsHeading: string;
  highlights: { title: string; text: string }[];
  sections: { heading: string; text: string }[];
}

export const AREA_PAGES: AreaPageContent[] = [
  {
    path: '/office-cleaning-fredericksburg-va',
    name: 'Fredericksburg',
    title: 'Office Cleaning in Fredericksburg, VA | Flip Ready',
    description:
      'After-hours office cleaning in Fredericksburg, VA for downtown offices, medical practices and the Route 3 corridor. Recurring plans, deep cleans, free quote.',
    h1: 'Office Cleaning in Fredericksburg, VA',
    intro:
      'Flip Ready is based in Fredericksburg, so the city is home turf. We clean offices after hours, on the schedule you choose, so your team and your clients walk into a fresh, professional space every morning.',
    highlightsHeading: 'Fredericksburg businesses we serve',
    highlights: [
      {
        title: 'Downtown professional offices',
        text: 'Law firms, accountants, real estate and insurance offices in the historic district along Caroline Street and William Street, where older buildings mean original floors, tight stairwells and small restrooms that need a careful hand.',
      },
      {
        title: 'Medical and dental practices',
        text: 'Practices and outpatient offices around Mary Washington Hospital and along the Route 1 corridor, where waiting rooms and restrooms are the first thing patients notice.',
      },
      {
        title: 'Office parks and shared workspaces',
        text: 'Offices off Route 3 near Central Park and along Cowan Boulevard, plus co-working spaces where a lot of people share the same desks, kitchens and conference rooms.',
      },
    ],
    sections: [
      {
        heading: 'Cleaning that works around your business hours',
        text: 'Nobody wants a crew vacuuming during a client meeting. We clean after you close, so your workday is never interrupted. Choose daily, weekly, bi-weekly or monthly service, and add a deep clean before an inspection, a move or a busy season.',
      },
      {
        heading: 'Across the city and just outside it',
        text: 'We cover downtown, the Route 1 and Route 3 corridors, Central Park, Celebrate Virginia and the area around the University of Mary Washington. Just over the city line, we also serve Stafford, Spotsylvania, King George and Caroline County.',
      },
    ],
  },
  {
    path: '/commercial-cleaning-stafford-va',
    name: 'Stafford',
    title: 'Commercial Cleaning in Stafford, VA | Flip Ready',
    description:
      'After-hours commercial and office cleaning in Stafford County, VA, from Falmouth to Garrisonville Road. Recurring plans, deep cleans and a free quote.',
    h1: 'Commercial Cleaning in Stafford, VA',
    intro:
      'Stafford County keeps growing, and so does the number of offices, clinics and shops along Route 1, Route 17 and Garrisonville Road. Flip Ready provides after-hours commercial cleaning across the county, from Falmouth in the south to the offices near Quantico in the north.',
    highlightsHeading: 'Stafford businesses we serve',
    highlights: [
      {
        title: 'Contractor and professional offices',
        text: 'Stafford is home to many government contractors and professional firms that work with Marine Corps Base Quantico. We clean after hours, so your staff never shares the floor with a cleaning crew.',
      },
      {
        title: 'Retail and service businesses',
        text: 'Storefronts, salons, fitness studios and service businesses in the shopping centers along Garrisonville Road and Route 1 that need a clean, welcoming space when the doors open.',
      },
      {
        title: 'Medical offices',
        text: 'Practices near Stafford Hospital and along Courthouse Road, where waiting rooms and restrooms shape a patient’s first impression.',
      },
    ],
    sections: [
      {
        heading: 'From Falmouth to North Stafford',
        text: 'Falmouth sits across the Rappahannock from downtown Fredericksburg, where Route 1 meets Route 17, and it’s one of the closest parts of the county to us. From there we cover the Warrenton Road (Route 17) corridor, the Stafford Courthouse area and the busy Garrisonville Road (Route 610) area in North Stafford.',
      },
      {
        heading: 'One provider for more than one location',
        text: 'Have an office in Stafford and another in Fredericksburg or Spotsylvania? Our multi-location service keeps every site to the same standard, with one point of contact and one invoice.',
      },
    ],
  },
  {
    path: '/commercial-cleaning-spotsylvania-va',
    name: 'Spotsylvania',
    title: 'Commercial Cleaning in Spotsylvania, VA | Flip Ready',
    description:
      'Commercial cleaning in Spotsylvania County, VA: Cosner’s Corner, Harrison Crossing, Massaponax and Route 3. After-hours and recurring plans. Free quote.',
    h1: 'Commercial Cleaning in Spotsylvania, VA',
    intro:
      'Spotsylvania County wraps around Fredericksburg to the south and west, and much of the region’s newer commercial space is here. Flip Ready cleans offices and commercial spaces across the county after hours, so you open every morning to a clean building.',
    highlightsHeading: 'Spotsylvania businesses we serve',
    highlights: [
      {
        title: 'Offices near the shopping centers',
        text: 'Professional offices and service businesses near Spotsylvania Towne Centre, Cosner’s Corner and Harrison Crossing, where a polished front counter and clean restrooms matter to walk-in customers.',
      },
      {
        title: 'Medical and professional suites',
        text: 'Medical, dental and professional offices in the newer buildings along Route 1 and Route 3, with waiting areas that need to be ready for each day’s first appointment.',
      },
      {
        title: 'Growing small businesses',
        text: 'Small offices along Courthouse Road (Route 208) and in Massaponax that want reliable recurring cleaning without hiring in-house staff.',
      },
    ],
    sections: [
      {
        heading: 'Route 3, Route 1 and Courthouse Road',
        text: 'We cover the county’s main commercial corridors: Plank Road (Route 3) from the Fredericksburg line out past Harrison Crossing, the Route 1 and Massaponax area around Cosner’s Corner, and Courthouse Road down toward Spotsylvania Courthouse.',
      },
      {
        heading: 'Locust Grove and western Route 3',
        text: 'Farther west, Route 3 runs toward Chancellor and Locust Grove, just over the Orange County line near Lake of the Woods. We serve businesses along that stretch too, so give us a call to set up a schedule for your location.',
      },
    ],
  },
  {
    path: '/office-cleaning-king-george-va',
    name: 'King George',
    title: 'Office Cleaning in King George, VA | Flip Ready',
    description:
      'After-hours office cleaning in King George, VA, including Dahlgren-area contractors and offices along Route 3 and Route 301. Recurring plans. Free quote.',
    h1: 'Office Cleaning in King George, VA',
    intro:
      'King George is a short drive east of Fredericksburg on Route 3, and a lot of its office work revolves around the Navy base at Dahlgren. Flip Ready provides after-hours office cleaning throughout the county.',
    highlightsHeading: 'King George businesses we serve',
    highlights: [
      {
        title: 'Dahlgren-area contractors',
        text: 'Engineering and defense-contractor offices that support Naval Surface Warfare Center Dahlgren Division, where a tidy, professional workspace reflects the work being done.',
      },
      {
        title: 'Kings Highway businesses',
        text: 'Professional offices and service businesses along Kings Highway (Route 3) near the county courthouse.',
      },
      {
        title: 'The Route 301 corridor',
        text: 'Businesses along Route 301 as it runs through the county toward the Potomac River crossing into Maryland.',
      },
    ],
    sections: [
      {
        heading: 'Regular service outside the city',
        text: 'Being outside the city shouldn’t mean settling for irregular cleaning. We schedule daily, weekly, bi-weekly or monthly service in King George, and we can add a deep clean for a seasonal refresh, a move or inspection prep.',
      },
      {
        heading: 'Easy to reach from Fredericksburg',
        text: 'Kings Highway (Route 3) links King George directly to Fredericksburg, which makes the county a natural part of our service area. Tell us where your office is and we’ll work out a schedule that fits.',
      },
    ],
  },
  {
    path: '/commercial-cleaning-caroline-county-va',
    name: 'Caroline County',
    title: 'Commercial Cleaning in Caroline County, VA | Flip Ready',
    description:
      'Commercial and office cleaning in Caroline County, VA: Bowling Green, Ladysmith, Ruther Glen and Carmel Church. After-hours and recurring plans. Free quote.',
    h1: 'Commercial Cleaning in Caroline County, VA',
    intro:
      'Caroline County sits just south of Fredericksburg along I-95 and Route 1, with a mix of small-town main streets and fast-growing interchange areas. Flip Ready provides after-hours commercial cleaning across the county.',
    highlightsHeading: 'Caroline County businesses we serve',
    highlights: [
      {
        title: 'Bowling Green offices',
        text: 'Professional offices around the town of Bowling Green, the county seat, where a small office still deserves a professional clean.',
      },
      {
        title: 'Ladysmith and Ruther Glen',
        text: 'Growing communities in the southern part of the county, with newer offices, clinics and service businesses near Route 1.',
      },
      {
        title: 'Carmel Church and I-95',
        text: 'Businesses around the Carmel Church interchange (I-95 exit 104) and along Route 207, where heavy foot traffic means floors and restrooms need steady attention.',
      },
    ],
    sections: [
      {
        heading: 'Covering a large, spread-out county',
        text: 'Caroline is one of the largest counties in the region by land area, and its businesses are spread between Port Royal on Route 301, Bowling Green and the I-95 corridor. Let us know where you are when you call, and we’ll set up a cleaning schedule for your location.',
      },
      {
        heading: 'Recurring service and one-time deep cleans',
        text: 'Choose daily, weekly, bi-weekly or monthly service, or book a one-time deep clean for a move-in, move-out or pre-inspection prep. Carpet steaming, window washing and post-renovation cleanup are available as extras.',
      },
    ],
  },
];
